import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import * as L from "lucide-react";

export function InboxScreen({ user, go }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/inbox");
      setItems(data.items || []);
    } catch { toast.error("Couldn't load inbox"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load();   }, []);

  const openItem = async (item) => {
    setOpenId(item.id);
    if (item.unread) {
      try {
        await api.patch(`/integrations/inbox/${item.id}/read`);
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, unread: false } : i));
      } catch { /* non-blocking */ }
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/integrations/inbox/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Couldn't delete"); }
  };

  const unread = items.filter(i => i.unread).length;

  return (
    <div className="p-5 space-y-4" data-testid="inbox-screen">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-2xl font-display">Inbox</div>
          <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
            Scheduled briefings and notes from Rammy land here.
          </p>
          <div className="mt-2 text-xs text-[var(--rammy-text-muted)]" data-testid="inbox-count">
            {unread > 0 ? `${unread} unread` : "All caught up"}
          </div>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold" data-testid="inbox-refresh">Refresh</button>
      </div>

      {loading && <div className="text-sm text-[var(--rammy-text-muted)] py-8 text-center">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-8 text-center" data-testid="inbox-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-[var(--rammy-surface-alt)] flex items-center justify-center mb-3">
            <L.Bell size={20} className="text-[var(--rammy-text-muted)]"/>
          </div>
          <div className="font-medium">Nothing yet.</div>
          <p className="text-xs text-[var(--rammy-text-muted)] mt-1 max-w-xs mx-auto leading-relaxed">
            Turn on the morning briefing in <button onClick={()=>go("settings")} className="underline hover:text-[var(--rammy-cyan-dark)]">Settings</button> and Rammy will drop a warm note here each day.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {items.map(i => {
          const open = openId === i.id;
          return (
            <div
              key={i.id}
              role="button"
              tabIndex={0}
              onClick={()=>openItem(i)}
              onKeyDown={(e)=>{ if(e.key === "Enter" || e.key === " ") { e.preventDefault(); openItem(i); } }}
              data-testid={`inbox-item-${i.id}`}
              data-unread={i.unread ? "true" : "false"}
              className={`w-full text-left rounded-2xl border p-4 transition cursor-pointer ${
                i.unread ? "border-[var(--rammy-cyan)]/40 bg-[var(--rammy-cyan)]/5" : "border-[var(--rammy-border)] bg-[var(--rm-surface)]"
              }`}
            >
              <div className="flex items-start gap-3">
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  i.unread ? "bg-[var(--rammy-cyan)]/25 text-[var(--rammy-cyan-dark)]" : "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)]"
                }`}>
                  <L.Sun size={16}/>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{i.title}</span>
                    {i.unread && <span className="w-1.5 h-1.5 rounded-full bg-[var(--rammy-cyan)]"/>}
                    {i.source === "scheduled" && <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)] px-1.5 py-0.5 rounded-full">Auto</span>}
                  </div>
                  <div className={`text-sm mt-1 leading-relaxed whitespace-pre-wrap ${open ? "" : "line-clamp-2"}`}>
                    {i.body}
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-[var(--rammy-text-muted)] font-mono">
                    <span>{new Date(i.created_at).toLocaleString()}</span>
                    {i.meta?.event_count !== undefined && (
                      <span>{i.meta.event_count} event{i.meta.event_count === 1 ? "" : "s"}</span>
                    )}
                    <button
                      onClick={(e)=>del(i.id, e)}
                      className="ml-auto text-[var(--rammy-text-muted)] hover:text-red-500"
                      data-testid={`inbox-delete-${i.id}`}
                      aria-label="Delete"
                    >
                      <L.Trash2 size={13}/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
